/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { ALL_PERMISSIONS } from "../../hooks/useTenantStorePermissions";

export const ONBOARDING_DRAFT_KEY = "onboardingCompanyDraft";
export const ONBOARDING_STEP_KEY = "onboardingStep";

const LOGO_MAX_DATA_URL = 1_800_000;

export function parseStorageJSON(raw, fallback = {}) {
  try {
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function setOnboardingStep(step) {
  if (step) localStorage.setItem(ONBOARDING_STEP_KEY, step);
}

export function getOnboardingStep() {
  return localStorage.getItem(ONBOARDING_STEP_KEY) || "";
}

export function loadCompanyInfoDraft() {
  return parseStorageJSON(localStorage.getItem(ONBOARDING_DRAFT_KEY), null);
}

export function saveCompanyInfoDraft(draft) {
  try {
    const payload = {
      storeName: draft.storeName || "",
      storeSlug: draft.storeSlug || "",
      sector: draft.sector || "",
      paymentMethods: Array.isArray(draft.paymentMethods) ? draft.paymentMethods : [],
      services: draft.services || "",
      logoDataUrl: draft.logoDataUrl || "",
      preview: draft.preview || draft.logoDataUrl || "",
      savedAt: Date.now(),
    };
    if (payload.logoDataUrl && payload.logoDataUrl.length > LOGO_MAX_DATA_URL) {
      payload.logoDataUrl = "";
      if (String(payload.preview).startsWith("data:") && payload.preview.length > LOGO_MAX_DATA_URL) {
        payload.preview = "";
      }
    }
    localStorage.setItem(ONBOARDING_DRAFT_KEY, JSON.stringify(payload));
  } catch (err) {
    console.warn("Could not persist onboarding draft:", err);
  }
}

export function clearCompanyInfoDraft() {
  localStorage.removeItem(ONBOARDING_DRAFT_KEY);
}

export const SIGNUP_DRAFT_KEY = "onboardingSignupDraft";

export function saveSignupDraft(draft) {
  try {
    localStorage.setItem(
      SIGNUP_DRAFT_KEY,
      JSON.stringify({
        fullName: draft.fullName || "",
        engFullName: draft.engFullName || "",
        phone: draft.phone || "",
        email: draft.email || "",
        savedAt: Date.now(),
      })
    );
  } catch {
    /* ignore quota */
  }
}

export function loadSignupDraft() {
  return parseStorageJSON(localStorage.getItem(SIGNUP_DRAFT_KEY), null);
}

export function clearSignupDraft() {
  localStorage.removeItem(SIGNUP_DRAFT_KEY);
}

export function clearOnboardingSession() {
  clearCompanyInfoDraft();
  clearSignupDraft();
  localStorage.removeItem(ONBOARDING_STEP_KEY);
}

export function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      resolve("");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function dataUrlToFile(dataUrl, filename = "store-logo.png") {
  if (!dataUrl || !String(dataUrl).startsWith("data:")) return null;
  try {
    const [header, base64] = String(dataUrl).split(",");
    const mime = (header.match(/data:(.*?);/) || [])[1] || "image/png";
    const binary = atob(base64 || "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch {
    return null;
  }
}

export function isAccountExistsError(message) {
  const m = String(message || "").toLowerCase();
  return (
    m.includes("already registered") ||
    m.includes("already exist") ||
    m.includes("email already") ||
    m.includes("already in use") ||
    m.includes("duplicate") ||
    m.includes("مسجل بالفعل") ||
    m.includes("مسجل مسبقا") ||
    m.includes("الحساب موجود") ||
    m.includes("موجود مسبقا") ||
    m.includes("البريد مستخدم")
  );
}

function pickUser(data) {
  const users = Array.isArray(data?.users)
    ? data.users
    : Array.isArray(data?.tenantUsersList)
      ? data.tenantUsersList
      : [];
  return users[0] || data?.tenantUser || data?.tenant || null;
}

export function persistTenantAuth(data, authLogin, { source } = {}) {
  const token = data?.token || "";
  const tenant = data?.tenant || {};
  const tenantDetails = data?.tenantDetails || {};
  const user = pickUser(data);
  const users = Array.isArray(data?.users)
    ? data.users
    : Array.isArray(data?.tenantUsersList)
      ? data.tenantUsersList
      : user
        ? [user]
        : [];

  if (!token) return false;

  const tenantId =
    tenant?.tenantId ||
    tenant?.TenantId ||
    tenantDetails?.tenantId ||
    tenantDetails?.TenantId ||
    user?.tenantId ||
    user?.TenantId ||
    "";

  localStorage.setItem("token", token);
  localStorage.setItem("authToken", token);
  localStorage.setItem("tenant", JSON.stringify(tenant || {}));
  localStorage.setItem("tenantId", tenantId ? String(tenantId) : "");
  localStorage.setItem(
    "tenantEmail",
    tenant.email || tenant.Email || user?.email || user?.Email || ""
  );
  localStorage.setItem(
    "tenantPhone",
    tenant.phone || tenant.Phone || user?.phone || user?.Phone || ""
  );
  localStorage.setItem("tenantUsersList", JSON.stringify(users));
  localStorage.setItem("tenantDB", tenant.databaseName || tenant.DatabaseName || "");

  if (tenantDetails && Object.keys(tenantDetails).length) {
    localStorage.setItem("tenantDetails", JSON.stringify(tenantDetails));
  }

  if (source === "register") {
    localStorage.setItem("fullRegisterResponse", JSON.stringify(data));
  } else if (source === "login") {
    localStorage.setItem("fullLoginResponse", JSON.stringify(data));
  }

  if (data?.plans) localStorage.setItem("plans", JSON.stringify(data.plans));
  if (data?.themes) localStorage.setItem("themes", JSON.stringify(data.themes));
  if (data?.paymentMethods) localStorage.setItem("paymentMethods", JSON.stringify(data.paymentMethods));

  const slug =
    tenant.storeSlug ||
    tenant.StoreSlug ||
    tenantDetails.storeSlug ||
    tenantDetails.StoreSlug ||
    "";
  if (slug) localStorage.setItem("companyStoreSlug", slug);

  const storeName = tenantDetails.clientName || tenantDetails.ClientName || "";
  if (storeName) localStorage.setItem("companyStoreName", storeName);

  const authUser =
    user && typeof user === "object"
      ? user
      : { email: tenant.email || tenant.Email, tenantId };

  if (typeof authLogin === "function") {
    authLogin({
      token,
      user: authUser,
      authRole: "tenant",
      isTenantOwner: true,
      permissions: ALL_PERMISSIONS,
    });
  }

  return true;
}

export function recoverTenantAuth(authLogin) {
  const token = localStorage.getItem("authToken") || localStorage.getItem("token") || "";
  if (!token) return false;

  if (!localStorage.getItem("authToken") && token) {
    localStorage.setItem("authToken", token);
  }

  let user = parseStorageJSON(localStorage.getItem("user"), null);
  if (!user || typeof user !== "object") {
    const registerData = parseStorageJSON(localStorage.getItem("fullRegisterResponse"), {});
    const loginData = parseStorageJSON(localStorage.getItem("fullLoginResponse"), {});
    user =
      pickUser(registerData) ||
      pickUser(loginData) ||
      parseStorageJSON(localStorage.getItem("tenant"), null);
  }

  if (!user || typeof user !== "object") return Boolean(token);

  if (typeof authLogin === "function") {
    const role = localStorage.getItem("authRole") || "tenant";
    const isTenantOwner = localStorage.getItem("isTenantOwner") !== "false";
    authLogin({
      token,
      user,
      authRole: role,
      isTenantOwner,
      permissions:
        parseStorageJSON(localStorage.getItem("permissions"), null) || ALL_PERMISSIONS,
    });
  }

  return true;
}

export function getOnboardingResumePath(tenantDetails) {
  const details =
    tenantDetails && typeof tenantDetails === "object"
      ? tenantDetails
      : parseStorageJSON(localStorage.getItem("tenantDetails"), {});

  const storeName =
    details.clientName ||
    details.ClientName ||
    details.companyName ||
    details.CompanyName ||
    localStorage.getItem("companyStoreName") ||
    "";
  const slug =
    details.storeSlug ||
    details.StoreSlug ||
    localStorage.getItem("companyStoreSlug") ||
    "";
  const sector =
    details.sectorType ||
    details.SectorType ||
    localStorage.getItem("companySector") ||
    "";
  const services =
    details.businessName ||
    details.BusinessName ||
    localStorage.getItem("companyServices") ||
    "";
  const themeId = details.themeId || details.ThemeId;
  const planId =
    details.planId ||
    details.PlanId ||
    details.mainPlanId ||
    details.MainPlanId ||
    details.detailPlanId ||
    details.DetailPlanId;

  const companyDone = Boolean(
    String(storeName).trim() &&
      String(slug).trim() &&
      String(sector).trim() &&
      String(services).trim()
  );
  const planDone = Boolean(themeId && planId);

  if (companyDone && planDone) return "/admin-store";
  if (companyDone) return "/theme-plan";

  const step = getOnboardingStep();
  if (step === "theme-plan" && companyDone) return "/theme-plan";
  return "/company-info";
}

export function hasOnboardingSession() {
  return Boolean(localStorage.getItem("authToken") || localStorage.getItem("token"));
}

export async function loginExistingTenant(email, password, baseUrl) {
  const res = await fetch(`${baseUrl}/Login/UserLogin`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      email: String(email || "").trim(),
      password,
    }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || data?.Message || `HTTP ${res.status}`);
  }
  return data;
}
